import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Checkbox, Col, DatePicker, Empty, Modal, Pagination, Row, Segmented, Select, Spin, Statistic, Table, Tag, Tooltip as AntTooltip, Typography, message } from 'antd';
import { ApartmentOutlined, ArrowDownOutlined, ArrowLeftOutlined, ArrowUpOutlined, EyeOutlined, ReloadOutlined, SwapOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import {
    CartesianGrid,
    LabelList,
    Legend,
    Line,
    LineChart,
    Treemap,
    ResponsiveContainer,
    Tooltip as RechartsTooltip,
    XAxis,
    YAxis
} from 'recharts';
import './NicheAnalysis.css';
import api from '../../services/api';
import ProductImagesGallery from '../../components/ProductImagesGallery/ProductImagesGallery';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#ffc658', '#8dd1e1'];
const OBJECT_OPTIONS = [
    { value: 'niche', label: 'Niche' },
    { value: 'productline', label: 'Product Line' },
    { value: 'category', label: 'Category' },
    { value: 'quote', label: 'Quote' }
];
const DAY_OPTIONS = [7, 30, 90].map(value => ({ value, label: `${value} days` }));
const ROLLING_PERIODS = [7, 30, 90];
const OVERVIEW_TOTAL_ID = '__overview_total__';
const TOP_ITEMS_BATCH_SIZE = 12;
const EXPAND_ALL_NICHES_STORAGE_KEY = 'nicheAnalysis.expandAllNiches';
const DEFAULT_TOP_ITEM_SORT = { field: 'currentSales', order: 'descend' };
const TOP_ITEM_SORT_FIELDS = ['sku', 'currentSales', 'monthComparison', 'yearComparison', 'rank'];
const TOP_ITEM_SORT_DIRECTIONS = ['descend', 'ascend'];
const DISTRIBUTION_OPTIONS = [
    { value: 'productline', label: 'Product Line' },
    { value: 'category', label: 'Category' },
    { value: 'amazon', label: 'Amazon' }
];
const formatDistributionTitle = (objectType) => {
    if (!objectType) return 'Distribution';
    const labels = {
        productline: 'Product Line',
        category: 'Category',
        amazon: 'Amazon',
        niche: 'Niche',
        quote: 'Quote'
    };
    const label = labels[objectType] || objectType;
    return `By ${label}`;
};

const formatMetricDate = (date) => {
    const [year, month, day] = String(date || '').split('-');
    return year && month && day ? `${month}/${day}/${year}` : date;
};

const formatDateParam = (value) => value ? dayjs(value).format('YYYY-MM-DD') : '';

const getRollingRange = (periodDays) => {
    const end = dayjs();
    return [end.subtract(periodDays - 1, 'day'), end];
};

const getInclusiveDays = (start, end) => {
    if (!start || !end) return 0;
    return Math.max(1, end.startOf('day').diff(start.startOf('day'), 'day') + 1);
};

const withChartColors = (rows = []) => rows.map((row, index) => ({
    ...row,
    color: row.color || COLORS[index % COLORS.length]
}));

const groupTinyChartRows = (rows = [], threshold = 0.5) => {
    const visibleRows = [];
    const tinyRows = [];

    rows.forEach(row => {
        const value = Number(row.value || 0);
        const percentage = Number(row.percentage || 0);
        if (value > 0 && !row.isOther && percentage <= threshold) {
            tinyRows.push(row);
            return;
        }
        visibleRows.push(row);
    });

    const tinyValue = tinyRows.reduce((sum, row) => sum + Number(row.value || 0), 0);
    if (tinyValue <= 0) return visibleRows;

    const tinyPercentage = tinyRows.reduce((sum, row) => sum + Number(row.percentage || 0), 0);
    return [
        ...visibleRows,
        {
            id: '__chart_tiny_segments__',
            name: 'NICHE OTHER <=0,5%',
            type: 'NICHE OTHER <=0,5%',
            value: tinyValue,
            percentage: Number(tinyPercentage.toFixed(1)),
            color: '#64748b',
            isTinyGroup: true,
            groupedRows: tinyRows.sort((left, right) => Number(right.value || 0) - Number(left.value || 0))
        }
    ].sort((left, right) => Number(right.value || 0) - Number(left.value || 0));
};


const truncateTreemapLabel = (value, maxLength = 18) => {
    const label = String(value || 'Unknown');
    return label.length > maxLength ? `${label.slice(0, Math.max(1, maxLength - 1))}…` : label;
};

const formatTreemapTooltip = (value, name, props) => {
    const payload = props?.payload || {};
    const sourcePath = getOverviewSourcePath(payload);
    const displayName = getOverviewDisplayName(payload) || name;
    const breadcrumb = sourcePath.length
        ? `${sourcePath.join(' / ')} / ${displayName}`
        : displayName;
    return [
        `${Number(value || 0).toLocaleString()} sales (${Number(payload.percentage || 0).toFixed(1)}%)`,
        breadcrumb
    ];
};

const formatOverviewPath = (item = {}) => (
    getOverviewSourcePath(item).length
        ? `${getOverviewSourcePath(item).join(' / ')} / ${getOverviewDisplayName(item)}`
        : getOverviewDisplayName(item)
);

const normalizeOverviewSourcePath = (value) => {
    if (Array.isArray(value)) return value.filter(Boolean);
    if (typeof value !== 'string') return [];
    const trimmed = value.trim();
    if (!trimmed) return [];
    const withoutBraces = trimmed.startsWith('{') && trimmed.endsWith('}')
        ? trimmed.slice(1, -1)
        : trimmed;
    return withoutBraces
        .split(',')
        .map(item => item.trim().replace(/^"(.*)"$/, '$1'))
        .filter(Boolean);
};

const getOverviewSourcePath = (item = {}) => {
    const sourcePath = normalizeOverviewSourcePath(item.sourcePath);
    if (sourcePath.length) {
        return sourcePath;
    }
    if (item.isOther && item.parentName) {
        return [item.parentName];
    }
    return [];
};

const getOverviewDisplayName = (item = {}) => (
    item.isOther && String(item.name || item.type || '').toLowerCase() === 'other'
        ? 'OTHER'
        : (item.name || item.type || '')
);

const renderOverviewPathTags = (item = {}, className = 'niche-source-path') => {
    const sourcePath = getOverviewSourcePath(item);
    if (!sourcePath.length) return null;

    return (
        <span className={className} title={sourcePath.join(' / ')}>
            {sourcePath.map((part, index) => (
                <React.Fragment key={`${part}-${index}`}>
                    <Tag className="niche-source-path-tag">{part}</Tag>
                    {index < sourcePath.length - 1 ? (
                        <span className="niche-source-path-separator">/</span>
                    ) : null}
                </React.Fragment>
            ))}
        </span>
    );
};

const formatPeriodComparisonValue = (value) => {
    if (value === null || value === undefined) return 'N/A';
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return 'N/A';
    return `${numericValue > 0 ? '+' : ''}${numericValue.toFixed(1)}%`;
};

const calculatePercentChange = (current, previous) => {
    const previousValue = Number(previous || 0);
    if (!previousValue) return null;
    return Number((((Number(current || 0) - previousValue) / previousValue) * 100).toFixed(1));
};

const isOverviewTotalItem = (item) => item?.id === OVERVIEW_TOTAL_ID || item?.isOverviewTotal;

const getInitialExpandAllNiches = () => {
    try {
        return window.localStorage.getItem(EXPAND_ALL_NICHES_STORAGE_KEY) === 'true';
    } catch (error) {
        return false;
    }
};

const addSalesTrendPeakLabels = (rows = [], spanDays = 30) => {
    if (!rows.length) return [];

    const values = rows.map(row => Number(row.salesCount || 0));
    const peakCandidates = values
        .map((value, index) => ({ index, value }))
        .filter(({ index, value }) => (
            index > 0
            && index < values.length - 1
            && value >= values[index - 1]
            && value > values[index + 1]
        ))
        .sort((a, b) => b.value - a.value);
    const globalPeakIndex = values.indexOf(Math.max(...values));
    if (!peakCandidates.some(peak => peak.index === globalPeakIndex)) {
        peakCandidates.unshift({ index: globalPeakIndex, value: values[globalPeakIndex] });
    }

    const labelLimit = spanDays <= 7 ? 2 : spanDays <= 30 ? 5 : 7;
    const minimumGap = spanDays <= 30 ? 2 : 4;
    const selectedIndexes = peakCandidates.reduce((selected, peak) => {
        if (
            selected.length < labelLimit
            && selected.every(index => Math.abs(index - peak.index) >= minimumGap)
        ) {
            selected.push(peak.index);
        }
        return selected;
    }, []);

    return rows.map((row, index) => ({
        ...row,
        peakLabel: selectedIndexes.includes(index)
            ? Math.round(values[index]).toLocaleString()
            : null
    }));
};

const createBubbleLayout = (rows = [], size = 1000) => {
    const padding = 18;
    const gap = 1.8;
    const circles = rows
        .map((row, index) => ({
            ...row,
            _bubbleOrder: index,
            r: Math.sqrt(Math.max(0, Number(row.value || 0)))
        }))
        .filter(row => row.r > 0)
        .sort((left, right) => Number(right.value || 0) - Number(left.value || 0));
    if (!circles.length) return [];
    if (circles.length === 1) {
        return [{
            ...circles[0],
            x: size / 2,
            y: size / 2,
            r: (size - padding * 2) / 2
        }];
    }

    const packRadius = (circle) => circle.r + gap;
    const intersects = (left, right) => {
        const dx = right.x - left.x;
        const dy = right.y - left.y;
        const distance = packRadius(left) + packRadius(right);
        return distance * distance > dx * dx + dy * dy + 1e-6;
    };
    const placeTangent = (left, right, circle) => {
        const dx = right.x - left.x;
        const dy = right.y - left.y;
        const distanceSquared = dx * dx + dy * dy;
        const leftRadius = packRadius(left) + packRadius(circle);
        const rightRadius = packRadius(right) + packRadius(circle);

        if (!distanceSquared) {
            circle.x = left.x + leftRadius;
            circle.y = left.y;
            return;
        }

        const x = (distanceSquared + leftRadius * leftRadius - rightRadius * rightRadius) / (2 * distanceSquared);
        const y = Math.sqrt(Math.max(0, leftRadius * leftRadius / distanceSquared - x * x));
        circle.x = left.x + x * dx + y * dy;
        circle.y = left.y + x * dy - y * dx;
    };
    const createNode = (circle) => ({
        circle,
        previous: null,
        next: null
    });
    const scoreNode = (node) => {
        const circle = node.circle;
        return circle.x * circle.x + circle.y * circle.y;
    };

    circles[0].x = 0;
    circles[0].y = 0;
    circles[1].x = packRadius(circles[0]) + packRadius(circles[1]);
    circles[1].y = 0;

    if (circles.length > 2) {
        placeTangent(circles[0], circles[1], circles[2]);
    }

    let nodeA = createNode(circles[0]);
    let nodeB = createNode(circles[1]);
    let nodeC = circles.length > 2 ? createNode(circles[2]) : null;

    if (nodeC) {
        nodeA.next = nodeC;
        nodeC.previous = nodeA;
        nodeC.next = nodeB;
        nodeB.previous = nodeC;
        nodeB.next = nodeA;
        nodeA.previous = nodeB;
        nodeA = nodeC;
        nodeB = nodeC.next;
    } else {
        nodeA.next = nodeB.previous = nodeB;
        nodeB.next = nodeA.previous = nodeA;
    }

    for (let index = 3; index < circles.length; index += 1) {
        const circle = circles[index];
        let placed = false;

        while (!placed) {
            placeTangent(nodeA.circle, nodeB.circle, circle);
            nodeC = createNode(circle);

            let forward = nodeB.next;
            let backward = nodeA.previous;
            let blocked = false;

            while (forward !== backward.next) {
                if (intersects(forward.circle, nodeC.circle)) {
                    nodeB = forward;
                    nodeA.next = nodeB;
                    nodeB.previous = nodeA;
                    blocked = true;
                    break;
                }
                forward = forward.next;

                if (intersects(backward.circle, nodeC.circle)) {
                    nodeA = backward;
                    nodeA.next = nodeB;
                    nodeB.previous = nodeA;
                    blocked = true;
                    break;
                }
                backward = backward.previous;
            }

            if (blocked) continue;

            nodeC.previous = nodeA;
            nodeC.next = nodeB;
            nodeA.next = nodeC;
            nodeB.previous = nodeC;
            nodeB = nodeC;

            let best = nodeC;
            let bestScore = scoreNode(best);
            let cursor = nodeC.next;
            while (cursor !== nodeC) {
                const score = scoreNode(cursor);
                if (score < bestScore) {
                    best = cursor;
                    bestScore = score;
                }
                cursor = cursor.next;
            }
            nodeA = best;
            nodeB = best.next;
            placed = true;
        }
    }

    const minX = Math.min(...circles.map(circle => circle.x - circle.r));
    const maxX = Math.max(...circles.map(circle => circle.x + circle.r));
    const minY = Math.min(...circles.map(circle => circle.y - circle.r));
    const maxY = Math.max(...circles.map(circle => circle.y + circle.r));
    const width = Math.max(1, maxX - minX);
    const height = Math.max(1, maxY - minY);
    const scale = (size - padding * 2) / Math.max(width, height);
    const offsetX = padding + (size - padding * 2 - width * scale) / 2 - minX * scale;
    const offsetY = padding + (size - padding * 2 - height * scale) / 2 - minY * scale;

    return circles
        .map(circle => ({
            ...circle,
            x: circle.x * scale + offsetX,
            y: circle.y * scale + offsetY,
            r: circle.r * scale
        }))
        .sort((left, right) => left._bubbleOrder - right._bubbleOrder);
};

const normalizeProductImages = (record = {}) => {
    const normalizeImage = (image, index = 0) => {
        if (!image) return null;
        if (typeof image === 'string') return { url: image, isMain: index === 0 };
        if (typeof image === 'object') {
            const url = image.url || image.link || image.src;
            return url ? { ...image, url, isMain: image.isMain || index === 0 } : null;
        }
        return null;
    };

    if (Array.isArray(record.images) && record.images.length > 0) {
        return record.images.map(normalizeImage).filter(Boolean);
    }
    const image = record.image || record.thumbnail || record.mainImage || record.imageUrl;
    const normalized = normalizeImage(image, 0);
    return normalized ? [normalized] : [];
};

const NicheAnalysis = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const initialDateFrom = searchParams.get('dateFrom') || '';
    const initialDateTo = searchParams.get('dateTo') || '';
    const initialDays = initialDateFrom && initialDateTo
        ? getInclusiveDays(dayjs(initialDateFrom), dayjs(initialDateTo))
        : (Number(searchParams.get('days')) || 30);
    const [objectType, setObjectType] = useState(searchParams.get('objectType') || 'niche');
    const [selectedIds, setSelectedIds] = useState(searchParams.get('ids') ? searchParams.get('ids').split(',') : []);
    const [days, setDays] = useState(initialDays);
    const [nicheIds, setNicheIds] = useState(searchParams.get('nicheIds') ? searchParams.get('nicheIds').split(',') : []);
    const [quoteIds, setQuoteIds] = useState(searchParams.get('quoteIds') ? searchParams.get('quoteIds').split(',') : []);
    const [categoryIds, setCategoryIds] = useState(searchParams.get('categoryIds') ? searchParams.get('categoryIds').split(',') : []);
    const [productLineIds, setProductLineIds] = useState(searchParams.get('productLineIds') ? searchParams.get('productLineIds').split(',') : []);
    const [dateFrom, setDateFrom] = useState(initialDateFrom);
    const [dateTo, setDateTo] = useState(initialDateTo);
    const [dateBounds, setDateBounds] = useState(null);
    const [objects, setObjects] = useState([]);
    const [filterObjects, setFilterObjects] = useState({ niche: [], quote: [], category: [], productLine: [] });
    const [filterObjectsLoading, setFilterObjectsLoading] = useState({});
    const objectOptionsCacheRef = useRef({});
    const objectOptionsRequestsRef = useRef({});
    const [overview, setOverview] = useState([]);
    const [overviewLoadedScopeKey, setOverviewLoadedScopeKey] = useState('');
    const [analysisRange, setAnalysisRange] = useState({ dateFrom: '', dateTo: '' });
    const overviewScopeTotalSalesRef = useRef(0);
    const [selectedItem, setSelectedItem] = useState(null);
    const [stats, setStats] = useState(null);
    const [trend, setTrend] = useState([]);
    const [topListings, setTopListings] = useState([]);
    const [topCompactSkus, setTopCompactSkus] = useState([]);
    const [topSkus, setTopSkus] = useState([]);
    const [topListingPage, setTopListingPage] = useState(1);
    const [topCompactSkuPage, setTopCompactSkuPage] = useState(1);
    const [topSkuPage, setTopSkuPage] = useState(1);
    const [topListingVisibleCount, setTopListingVisibleCount] = useState(TOP_ITEMS_BATCH_SIZE);
    const [topCompactSkuVisibleCount, setTopCompactSkuVisibleCount] = useState(TOP_ITEMS_BATCH_SIZE);
    const [topSkuVisibleCount, setTopSkuVisibleCount] = useState(TOP_ITEMS_BATCH_SIZE);
    const [topListingSort, setTopListingSort] = useState(DEFAULT_TOP_ITEM_SORT);
    const [topCompactSkuSort, setTopCompactSkuSort] = useState(DEFAULT_TOP_ITEM_SORT);
    const [topSkuSort, setTopSkuSort] = useState(DEFAULT_TOP_ITEM_SORT);
    const [topQuotes, setTopQuotes] = useState([]);
    const [analysisDetailsLoadedKey, setAnalysisDetailsLoadedKey] = useState('');
    const [autoCompareActivatedKey, setAutoCompareActivatedKey] = useState('');
    const [distributions, setDistributions] = useState([]);
    const distributionCacheRef = useRef({});
    const distributionRequestRef = useRef(0);
    const [quoteDistribution, setQuoteDistribution] = useState(null);
    const [distributionMode, setDistributionMode] = useState('productline');
    const [loadingDistribution, setLoadingDistribution] = useState(false);
    const [loadingQuoteDistribution, setLoadingQuoteDistribution] = useState(false);
    const [loadingOverview, setLoadingOverview] = useState(false);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [chartModal, setChartModal] = useState({ open: false, title: '', data: [] });
    const [expandedChildrenById, setExpandedChildrenById] = useState({});
    const [overviewExpansionHistory, setOverviewExpansionHistory] = useState([]);
    const [overviewExpandRequest, setOverviewExpandRequest] = useState(null);
    const [expandingOverviewId, setExpandingOverviewId] = useState(null);
    const [overviewComparisons, setOverviewComparisons] = useState({});
    const [loadingOverviewComparisons, setLoadingOverviewComparisons] = useState(false);
    const overviewComparisonRequestRef = useRef(0);
    const [overviewDetailOpen, setOverviewDetailOpen] = useState(false);
    const [overviewDetailView, setOverviewDetailView] = useState('treemap');
    const [hoveredBubble, setHoveredBubble] = useState(null);
    const [chartDays, setChartDays] = useState(30);
    const [chartScope, setChartScope] = useState(null);
    const [chartVisibleMetrics, setChartVisibleMetrics] = useState(['sales', 'rank', 'price']);
    const [showComparisonTrend, setShowComparisonTrend] = useState(false);
    const [comparisonTrendMode, setComparisonTrendMode] = useState('30');
    const [comparisonTrendRange, setComparisonTrendRange] = useState(null);
    const [comparisonTrend, setComparisonTrend] = useState([]);
    const [loadingComparisonTrend, setLoadingComparisonTrend] = useState(false);
    const comparisonTrendRequestRef = useRef(0);
    const [expandAllNiches, setExpandAllNiches] = useState(getInitialExpandAllNiches);
    const [expandedTinyGroups, setExpandedTinyGroups] = useState({});

    const displayOverview = useMemo(() => {
        const flattenRows = (rows = []) => rows.flatMap(row => {
            const children = expandedChildrenById[String(row.id)];
            return children ? flattenRows(children) : [row];
        });
        return withChartColors(flattenRows(overview));
    }, [expandedChildrenById, overview]);
    const chartOverviewRows = useMemo(
        () => groupTinyChartRows(displayOverview),
        [displayOverview]
    );
    const cardOverviewRows = useMemo(() => chartOverviewRows.flatMap(row => {
        if (!row.isTinyGroup || !expandedTinyGroups[String(row.id)]) return [row];
        const groupedRows = (row.groupedRows || []).map(child => ({
            ...child,
            _isTinyGroupChild: true,
            _tinyGroupParentId: row.id
        }));
        return [row, ...withChartColors(groupedRows)];
    }), [chartOverviewRows, expandedTinyGroups]);
    const overviewDetailChartRows = useMemo(
        () => chartOverviewRows.filter(row => Number(row.value || 0) > 0),
        [chartOverviewRows]
    );
    const modalTreemapRows = overviewDetailChartRows;
    const distributionTotalSales = useMemo(
        () => chartOverviewRows.reduce((sum, row) => sum + Number(row.value || 0), 0),
        [chartOverviewRows]
    );
    const topListingRows = useMemo(
        () => topListings.map(item => ({ ...item, _chartLevel: 'parent' })),
        [topListings]
    );
    const topCompactSkuRows = useMemo(
        () => topCompactSkus.map(item => ({ ...item, _chartLevel: 'grouped' })),
        [topCompactSkus]
    );
    const topSkuRows = useMemo(
        () => topSkus.map(item => ({ ...item, _chartLevel: 'leaf' })),
        [topSkus]
    );
    const visibleTopListingRows = useMemo(
        () => topListingRows.slice((topListingPage - 1) * TOP_ITEMS_BATCH_SIZE, ((topListingPage - 1) * TOP_ITEMS_BATCH_SIZE) + topListingVisibleCount),
        [topListingPage, topListingRows, topListingVisibleCount]
    );
    const visibleTopCompactSkuRows = useMemo(
        () => topCompactSkuRows.slice((topCompactSkuPage - 1) * TOP_ITEMS_BATCH_SIZE, ((topCompactSkuPage - 1) * TOP_ITEMS_BATCH_SIZE) + topCompactSkuVisibleCount),
        [topCompactSkuPage, topCompactSkuRows, topCompactSkuVisibleCount]
    );
    const visibleTopSkuRows = useMemo(
        () => topSkuRows.slice((topSkuPage - 1) * TOP_ITEMS_BATCH_SIZE, ((topSkuPage - 1) * TOP_ITEMS_BATCH_SIZE) + topSkuVisibleCount),
        [topSkuPage, topSkuRows, topSkuVisibleCount]
    );
    const overviewComparisonItems = useMemo(() => chartOverviewRows.map((row, index) => {
        const sources = (row.isTinyGroup ? row.groupedRows || [] : [row])
            .filter(source => source?.id)
            .map(source => ({
                id: source.id,
                parentNicheId: source.parentNicheId || null,
                isOther: Boolean(source.isOther)
            }));
        return {
            key: String(row.id || `row-${index}`),
            currentValue: Number(row.value || 0),
            sources
        };
    }).filter(item => item.sources.length), [chartOverviewRows]);
    const expandedTinyComparisonItems = useMemo(() => cardOverviewRows
        .filter(row => row._isTinyGroupChild && row.id)
        .map(row => ({
            key: String(row.id),
            currentValue: Number(row.value || 0),
            sources: [{
                id: row.id,
                parentNicheId: row.parentNicheId || null,
                isOther: Boolean(row.isOther)
            }]
        })), [cardOverviewRows]);
    const distributionTotalComparison = useMemo(() => {
        if (!overviewComparisonItems.length) return { previousValue: 0, periodComparison: null };
        let hasLoadedComparison = false;
        const previousValue = overviewComparisonItems.reduce((sum, item) => {
            const comparisonRow = overviewComparisons[item.key];
            if (!comparisonRow) return sum;
            hasLoadedComparison = true;
            return sum + Number(comparisonRow.previousValue || 0);
        }, 0);
        return {
            previousValue,
            periodComparison: hasLoadedComparison
                ? calculatePercentChange(distributionTotalSales, previousValue)
                : null
        };
    }, [distributionTotalSales, overviewComparisonItems, overviewComparisons]);
    const comparisonPeriodDays = useMemo(() => {
        if (dateFrom && dateTo) {
            return getInclusiveDays(dayjs(dateFrom), dayjs(dateTo));
        }
        return days;
    }, [dateFrom, dateTo, days]);
    const comparisonPeriodLabel = `vs prev ${comparisonPeriodDays}d`;
    const modalBubbleNodes = useMemo(
        () => createBubbleLayout(modalTreemapRows),
        [modalTreemapRows]
    );
    const periodRangeValue = useMemo(() => {
        if (dateFrom && dateTo) return [dayjs(dateFrom), dayjs(dateTo)];
        return getRollingRange(days);
    }, [dateFrom, dateTo, days]);
    const overviewScopeKey = useMemo(() => JSON.stringify({
        objectType,
        selectedIds,
        days,
        nicheIds,
        quoteIds,
        categoryIds,
        productLineIds,
        dateFrom,
        dateTo,
        expandAllNiches: objectType === 'niche' ? expandAllNiches : false
    }), [categoryIds, dateFrom, dateTo, days, expandAllNiches, nicheIds, objectType, productLineIds, quoteIds, selectedIds]);
    const analysisScopeKey = useMemo(() => {
        if (!selectedItem?.id) return '';
        return JSON.stringify({
            overviewScopeKey,
            selectedItemId: selectedItem.id,
            isOverviewTotal: isOverviewTotalItem(selectedItem)
        });
    }, [overviewScopeKey, selectedItem]);
    const isShortDateRangeFilter = useMemo(() => {
        if (!dateFrom || !dateTo) return false;
        return getInclusiveDays(dayjs(dateFrom), dayjs(dateTo)) < 7;
    }, [dateFrom, dateTo]);
    const periodPresets = useMemo(() => {
        const presets = ROLLING_PERIODS.map(periodDays => ({
            label: `${periodDays} days`,
            value: getRollingRange(periodDays)
        }));
        if (dateBounds?.dateFrom && dateBounds?.dateTo) {
            presets.push({
                label: 'Full time',
                value: [dayjs(dateBounds.dateFrom), dayjs(dateBounds.dateTo)]
            });
        }
        return presets;
    }, [dateBounds]);

    const handleAnalyzeOverviewItem = useCallback((item) => {
        if (!item || item.isOther || item.isTinyGroup) return;
        setDistributionMode('productline');
        setDistributions([]);
        setComparisonTrend([]);
        setSelectedItem(item);
    }, []);

    const handleAnalyzeOverviewTotal = useCallback(() => {
        setDistributionMode('productline');
        setDistributions([]);
        setQuoteDistribution(null);
        setComparisonTrend([]);
        setSelectedItem({
            id: OVERVIEW_TOTAL_ID,
            name: 'Total Sales',
            value: distributionTotalSales,
            percentage: 100,
            isOverviewTotal: true
        });
    }, [distributionTotalSales]);

    const renderTreemapContent = useCallback((props) => {
        const {
            x,
            y,
            width,
            height,
            name,
            value,
            percentage,
            color,
            id,
            isOther,
            isTinyGroup,
            hasChildren
        } = props;
        const cellWidth = Math.max(0, Number(width || 0));
        const cellHeight = Math.max(0, Number(height || 0));
        if (cellWidth <= 1 || cellHeight <= 1) return null;

        const canExpand = objectType === 'niche' && id && !isOther && !isTinyGroup && hasChildren;
        const canAnalyze = objectType !== 'niche' && id && !isOther && !isTinyGroup;
        const isSelected = id && String(selectedItem?.id) === String(id);
        const isExpanding = id && String(expandingOverviewId) === String(id);
        const computedPercentage = Number(percentage || 0);
        const compactLabel = cellWidth < 82 || cellHeight < 48;
        const labelX = x + (compactLabel ? 6 : 10);
        const labelTop = y + (compactLabel ? 14 : 17);
        const lineGap = compactLabel ? 13 : 18;
        const showName = cellWidth >= 38 && cellHeight >= 18;
        const showShare = cellWidth >= 48 && cellHeight >= 34;
        const showSales = cellWidth >= 74 && cellHeight >= 50;
        const displaySourcePath = getOverviewSourcePath(props);
        const showPath = displaySourcePath.length > 0 && cellWidth >= 92 && cellHeight >= 68;
        const labelLength = Math.max(4, Math.floor((cellWidth - (compactLabel ? 12 : 20)) / (compactLabel ? 6 : 7)));
        const pathLabel = displaySourcePath.join(' / ');
        const nameY = showPath ? labelTop + lineGap : labelTop;
        const shareY = nameY + lineGap;
        const salesY = shareY + (compactLabel ? 12 : 18);

        return (
            <g
                className={`overview-treemap-node ${canExpand || canAnalyze ? 'clickable' : ''} ${isSelected ? 'selected' : ''} ${isExpanding ? 'expanding' : ''}`}
                onClick={() => {
                    if (canExpand) {
                        setOverviewExpandRequest(props);
                        return;
                    }
                    if (canAnalyze) handleAnalyzeOverviewItem(props);
                }}
            >
                <rect
                    x={x}
                    y={y}
                    width={cellWidth}
                    height={cellHeight}
                    rx={8}
                    ry={8}
                    fill={color || COLORS[0]}
                    stroke="#ffffff"
                    strokeWidth={2}
                />
                {showName ? (
                    <>
                        {showPath ? (
                            <text x={labelX} y={labelTop} className="overview-treemap-label overview-treemap-label-path">
                                {truncateTreemapLabel(pathLabel, Math.max(6, labelLength - 1))}
                            </text>
                        ) : null}
                        <text x={labelX} y={nameY} className="overview-treemap-label overview-treemap-label-name">
                            {truncateTreemapLabel(getOverviewDisplayName(props) || name, labelLength)}
                        </text>
                        {showShare ? (
                            <text x={labelX} y={shareY} className="overview-treemap-label overview-treemap-label-share">
                                {`${computedPercentage.toFixed(1)}%`}
                            </text>
                        ) : null}
                        {showSales ? (
                            <text x={labelX} y={salesY} className="overview-treemap-label overview-treemap-label-sales">
                                {`${Number(value || 0).toLocaleString()} sales`}
                            </text>
                        ) : null}
                    </>
                ) : null}
            </g>
        );
    }, [expandingOverviewId, handleAnalyzeOverviewItem, objectType, selectedItem?.id]);

    const salesTrendChartData = useMemo(() => {
        return addSalesTrendPeakLabels(trend, days);
    }, [days, trend]);
    const comparisonTrendChartData = useMemo(() => {
        const spanDays = comparisonTrendMode === 'custom' && comparisonTrendRange?.[0] && comparisonTrendRange?.[1]
            ? getInclusiveDays(comparisonTrendRange[0], comparisonTrendRange[1])
            : Number(comparisonTrendMode || 30);
        return addSalesTrendPeakLabels(comparisonTrend, spanDays);
    }, [comparisonTrend, comparisonTrendMode, comparisonTrendRange]);

    const updateUrl = useCallback((next = {}) => {
        const params = new URLSearchParams();
        params.set('objectType', next.objectType || objectType);
        params.set('days', String(next.days || days));
        const ids = next.selectedIds !== undefined ? next.selectedIds : selectedIds;
        if (ids && ids.length > 0) params.set('ids', ids.join(','));
        const nextNicheIds = next.nicheIds !== undefined ? next.nicheIds : nicheIds;
        const nextQuoteIds = next.quoteIds !== undefined ? next.quoteIds : quoteIds;
        const nextCategoryIds = next.categoryIds !== undefined ? next.categoryIds : categoryIds;
        const nextProductLineIds = next.productLineIds !== undefined ? next.productLineIds : productLineIds;
        const nextDateFrom = next.dateFrom !== undefined ? next.dateFrom : dateFrom;
        const nextDateTo = next.dateTo !== undefined ? next.dateTo : dateTo;
        if (nextNicheIds?.length) params.set('nicheIds', nextNicheIds.join(','));
        if (nextQuoteIds?.length) params.set('quoteIds', nextQuoteIds.join(','));
        if (nextCategoryIds?.length) params.set('categoryIds', nextCategoryIds.join(','));
        if (nextProductLineIds?.length) params.set('productLineIds', nextProductLineIds.join(','));
        if (nextDateFrom) params.set('dateFrom', nextDateFrom);
        if (nextDateTo) params.set('dateTo', nextDateTo);
        setSearchParams(params, { replace: true });
    }, [categoryIds, dateFrom, dateTo, days, nicheIds, objectType, productLineIds, quoteIds, selectedIds, setSearchParams]);

    const fetchObjectOptions = useCallback(async (type) => {
        if (objectOptionsCacheRef.current[type]) {
            return objectOptionsCacheRef.current[type];
        }
        if (objectOptionsRequestsRef.current[type]) {
            return objectOptionsRequestsRef.current[type];
        }

        setFilterObjectsLoading(current => ({ ...current, [type]: true }));
        const request = api.get(type === 'productLine'
            ? '/product-lines?page=1&pageSize=500&isActive=true'
            : `/analytics/objects?objectType=${type}&limit=500`)
            .then(response => {
                const rows = type === 'productLine'
                    ? (response.data?.rows || [])
                    : (response.data || []);
                objectOptionsCacheRef.current[type] = rows;
                setFilterObjects(current => ({ ...current, [type]: rows }));
                return rows;
            })
            .finally(() => {
                delete objectOptionsRequestsRef.current[type];
                setFilterObjectsLoading(current => ({ ...current, [type]: false }));
            });
        objectOptionsRequestsRef.current[type] = request;
        return request;
    }, []);

    const loadObjects = useCallback(async (type) => {
        try {
            setObjects(await fetchObjectOptions(type));
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to load analysis objects');
        }
    }, [fetchObjectOptions]);

    const appendScopeParams = useCallback((params) => {
        if (nicheIds.length > 0) params.set('nicheIds', nicheIds.join(','));
        if (quoteIds.length > 0) params.set('quoteIds', quoteIds.join(','));
        if (categoryIds.length > 0) params.set('categoryIds', categoryIds.join(','));
        if (productLineIds.length > 0) params.set('productLineIds', productLineIds.join(','));
        return params;
    }, [categoryIds, nicheIds, productLineIds, quoteIds]);

    const appendGlobalParams = useCallback((params) => {
        appendScopeParams(params);
        if (dateFrom && dateTo) {
            params.set('dateFrom', dateFrom);
            params.set('dateTo', dateTo);
        }
        return params;
    }, [appendScopeParams, dateFrom, dateTo]);

    const appendOverviewTotalParams = useCallback((params) => {
        params.set('objectType', objectType);
        if (selectedIds.length > 0) params.set('ids', selectedIds.join(','));
        return appendGlobalParams(params);
    }, [appendGlobalParams, objectType, selectedIds]);

    const appendComparisonScopeParams = useCallback((params) => {
        params.set('objectType', objectType);
        if (selectedIds.length > 0) params.set('ids', selectedIds.join(','));
        return appendScopeParams(params);
    }, [appendScopeParams, objectType, selectedIds]);

    const attachOverviewSource = useCallback((rows = [], sourcePath = []) => rows.map(row => ({
        ...row,
        sourcePath: normalizeOverviewSourcePath(row.sourcePath).length
            ? normalizeOverviewSourcePath(row.sourcePath)
            : sourcePath
    })), []);

    const fetchOverviewRows = useCallback(async (parentId = null, sourcePath = []) => {
        const params = new URLSearchParams({ objectType, days: String(days) });
        if (selectedIds.length > 0) params.set('ids', selectedIds.join(','));
        if (objectType === 'niche' && parentId) {
            params.set('parentNicheId', parentId);
            if (overviewScopeTotalSalesRef.current > 0) params.set('scopeTotalSales', String(overviewScopeTotalSalesRef.current));
        } else if (objectType === 'niche' && expandAllNiches) {
            params.set('expandAll', 'true');
        }
        const response = await api.get(`/analytics/overview?${appendGlobalParams(params).toString()}`);
        setAnalysisRange({
            dateFrom: response.data?.dateFrom || '',
            dateTo: response.data?.dateTo || ''
        });
        return {
            rows: attachOverviewSource(response.data?.rows || [], sourcePath),
            scopeTotalSales: Number(response.data?.scopeTotalSales || 0)
        };
    }, [appendGlobalParams, attachOverviewSource, days, expandAllNiches, objectType, selectedIds]);

    const loadOverview = useCallback(async () => {
        setLoadingOverview(true);
        setOverviewLoadedScopeKey('');
        setExpandingOverviewId(null);
        setOverviewComparisons({});
        setLoadingOverviewComparisons(false);
        try {
            const { rows, scopeTotalSales } = await fetchOverviewRows();
            setOverview(rows);
            setOverviewLoadedScopeKey(overviewScopeKey);
            overviewScopeTotalSalesRef.current = scopeTotalSales;
            setExpandedChildrenById({});
            setOverviewExpansionHistory([]);
            setExpandedTinyGroups({});
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to load overview');
        } finally {
            setLoadingOverview(false);
        }
    }, [fetchOverviewRows, overviewScopeKey]);

    const loadOverviewComparisons = useCallback(async (items) => {
        const requestId = overviewComparisonRequestRef.current + 1;
        overviewComparisonRequestRef.current = requestId;

        if (!items.length) {
            setOverviewComparisons({});
            setLoadingOverviewComparisons(false);
            return;
        }

        setLoadingOverviewComparisons(true);
        setOverviewComparisons({});

        try {
            const payload = {
                objectType,
                days,
                items
            };
            if (selectedIds.length > 0) payload.ids = selectedIds;
            if (nicheIds.length > 0) payload.nicheIds = nicheIds;
            if (quoteIds.length > 0) payload.quoteIds = quoteIds;
            if (categoryIds.length > 0) payload.categoryIds = categoryIds;
            if (productLineIds.length > 0) payload.productLineIds = productLineIds;
            if (dateFrom && dateTo) {
                payload.dateFrom = dateFrom;
                payload.dateTo = dateTo;
            }

            const response = await api.post('/analytics/overview/comparison', payload);
            if (overviewComparisonRequestRef.current !== requestId) return;

            const nextComparisons = {};
            (response.data?.rows || []).forEach(row => {
                nextComparisons[String(row.key)] = row;
            });
            setOverviewComparisons(nextComparisons);
        } catch (error) {
            if (overviewComparisonRequestRef.current === requestId) {
                setOverviewComparisons({});
            }
        } finally {
            if (overviewComparisonRequestRef.current === requestId) {
                setLoadingOverviewComparisons(false);
            }
        }
    }, [categoryIds, dateFrom, dateTo, days, nicheIds, objectType, productLineIds, quoteIds, selectedIds]);

    const loadDetails = useCallback(async (item) => {
        if (!item?.id) return;
        const requestAnalysisKey = analysisScopeKey;
        setAnalysisDetailsLoadedKey('');
        setLoadingDetails(true);
        try {
            const params = new URLSearchParams({
                days: String(days),
                listingLimit: '100',
                compactSkuLimit: '100',
                skuLimit: '100',
                quoteLimit: '20',
                listingSortBy: topListingSort.field,
                listingSortDir: topListingSort.order === 'ascend' ? 'asc' : 'desc',
                compactSkuSortBy: topCompactSkuSort.field,
                compactSkuSortDir: topCompactSkuSort.order === 'ascend' ? 'asc' : 'desc',
                skuSortBy: topSkuSort.field,
                skuSortDir: topSkuSort.order === 'ascend' ? 'asc' : 'desc'
            });
            const url = isOverviewTotalItem(item)
                ? `/analytics/overview/details?${appendOverviewTotalParams(params).toString()}`
                : `/analytics/${objectType}/${encodeURIComponent(item.id)}/details?${appendGlobalParams(params).toString()}`;
            const response = await api.get(url);
            const data = response.data || {};
            setStats(data.stats || {});
            setTrend(data.trend || []);
            setTopListings(data.listings || []);
            setTopCompactSkus(data.compactSkus || []);
            setTopSkus(data.skus || []);
            setTopQuotes(data.quotes || []);
            setAnalysisDetailsLoadedKey(requestAnalysisKey);
        } catch (error) {
            setAnalysisDetailsLoadedKey('');
            message.error(error.response?.data?.message || 'Failed to load analysis details');
        } finally {
            setLoadingDetails(false);
        }
    }, [
        analysisScopeKey,
        appendGlobalParams,
        appendOverviewTotalParams,
        days,
        objectType,
        topListingSort.field,
        topListingSort.order,
        topCompactSkuSort.field,
        topCompactSkuSort.order,
        topSkuSort.field,
        topSkuSort.order
    ]);

    const loadDistribution = useCallback(async (item, mode = distributionMode) => {
        if (!item?.id) return;
        const params = new URLSearchParams({
            days: String(days),
            distributionType: mode
        });
        const requestParams = isOverviewTotalItem(item)
            ? appendOverviewTotalParams(params)
            : appendGlobalParams(params);
        const cacheKey = `${isOverviewTotalItem(item) ? 'overview' : objectType}:${item.id}:${requestParams.toString()}`;
        const cachedSection = distributionCacheRef.current[cacheKey];
        const requestId = distributionRequestRef.current + 1;
        distributionRequestRef.current = requestId;

        if (cachedSection) {
            setDistributions(previous => [
                ...previous.filter(section => section.objectType !== mode),
                cachedSection
            ]);
            return;
        }

        setLoadingDistribution(true);
        try {
            const response = await api.get(
                isOverviewTotalItem(item)
                    ? `/analytics/overview/distribution?${requestParams.toString()}`
                    : `/analytics/${objectType}/${encodeURIComponent(item.id)}/distribution?${requestParams.toString()}`
            );
            if (distributionRequestRef.current !== requestId) return;
            const section = response.data?.distributions?.[0] || { objectType: mode, rows: [] };
            distributionCacheRef.current[cacheKey] = section;
            setDistributions(previous => [
                ...previous.filter(item => item.objectType !== section.objectType),
                section
            ]);
        } catch (error) {
            if (distributionRequestRef.current === requestId) {
                message.error(error.response?.data?.message || 'Failed to load distribution');
            }
        } finally {
            if (distributionRequestRef.current === requestId) {
                setLoadingDistribution(false);
            }
        }
    }, [appendGlobalParams, appendOverviewTotalParams, days, distributionMode, objectType]);

    const loadQuoteDistribution = useCallback(async (item) => {
        if (!item?.id) return;
        setLoadingQuoteDistribution(true);
        try {
            const params = new URLSearchParams({
                days: String(days),
                distributionType: 'quote'
            });
            const requestParams = isOverviewTotalItem(item)
                ? appendOverviewTotalParams(params)
                : appendGlobalParams(params);
            const response = await api.get(
                isOverviewTotalItem(item)
                    ? `/analytics/overview/distribution?${requestParams.toString()}`
                    : `/analytics/${objectType}/${encodeURIComponent(item.id)}/distribution?${requestParams.toString()}`
            );
            setQuoteDistribution(response.data?.distributions?.[0] || null);
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to load quote distribution');
            setQuoteDistribution(null);
        } finally {
            setLoadingQuoteDistribution(false);
        }
    }, [appendGlobalParams, appendOverviewTotalParams, days, objectType]);

    const loadComparisonTrend = useCallback(async (item) => {
        if (!item?.id || !showComparisonTrend) return;
        if (comparisonTrendMode === 'custom' && (!comparisonTrendRange?.[0] || !comparisonTrendRange?.[1])) {
            setComparisonTrend([]);
            return;
        }

        const requestId = comparisonTrendRequestRef.current + 1;
        comparisonTrendRequestRef.current = requestId;
        setLoadingComparisonTrend(true);
        try {
            const params = new URLSearchParams();
            if (comparisonTrendMode === 'custom') {
                params.set('dateFrom', formatDateParam(comparisonTrendRange[0]));
                params.set('dateTo', formatDateParam(comparisonTrendRange[1]));
            } else {
                params.set('days', comparisonTrendMode);
            }
            const requestParams = appendComparisonScopeParams(params);

            const response = await api.get(
                isOverviewTotalItem(item)
                    ? `/analytics/overview/trend?${requestParams.toString()}`
                    : `/analytics/${objectType}/${encodeURIComponent(item.id)}/trend?${requestParams.toString()}`
            );
            if (comparisonTrendRequestRef.current !== requestId) return;
            setComparisonTrend(response.data || []);
        } catch (error) {
            if (comparisonTrendRequestRef.current === requestId) {
                message.error(error.response?.data?.message || 'Failed to load comparison trend');
                setComparisonTrend([]);
            }
        } finally {
            if (comparisonTrendRequestRef.current === requestId) {
                setLoadingComparisonTrend(false);
            }
        }
    }, [appendComparisonScopeParams, comparisonTrendMode, comparisonTrendRange, objectType, showComparisonTrend]);

    useEffect(() => {
        api.get('/analytics/date-bounds')
            .then(response => setDateBounds(response.data || null))
            .catch(() => setDateBounds(null));
    }, []);

    useEffect(() => {
        try {
            window.localStorage.setItem(EXPAND_ALL_NICHES_STORAGE_KEY, expandAllNiches ? 'true' : 'false');
        } catch (error) {
            // Local storage can be unavailable in private or restricted browser modes.
        }
    }, [expandAllNiches]);

    useEffect(() => {
        loadObjects(objectType);
    }, [loadObjects, objectType]);

    useEffect(() => {
        if (nicheIds.length > 0) fetchObjectOptions('niche').catch(() => undefined);
        if (quoteIds.length > 0) fetchObjectOptions('quote').catch(() => undefined);
        if (categoryIds.length > 0) fetchObjectOptions('category').catch(() => undefined);
        if (productLineIds.length > 0) fetchObjectOptions('productLine').catch(() => undefined);
    }, [categoryIds.length, fetchObjectOptions, nicheIds.length, productLineIds.length, quoteIds.length]);

    useEffect(() => {
        loadOverview();
        updateUrl();
    }, [loadOverview]);

    useEffect(() => {
        if (loadingOverview) return;
        loadOverviewComparisons([...overviewComparisonItems, ...expandedTinyComparisonItems]);
    }, [expandedTinyComparisonItems, loadOverviewComparisons, loadingOverview, overviewComparisonItems]);

    useEffect(() => {
        if (loadingOverview || overviewLoadedScopeKey !== overviewScopeKey || selectedItem?.id || !displayOverview.length) return;
        handleAnalyzeOverviewTotal();
    }, [displayOverview.length, handleAnalyzeOverviewTotal, loadingOverview, overviewLoadedScopeKey, overviewScopeKey, selectedItem?.id]);

    useEffect(() => {
        if (selectedItem?.id) loadDetails(selectedItem);
    }, [selectedItem?.id, loadDetails]);

    useEffect(() => {
        setTopListingPage(1);
        setTopCompactSkuPage(1);
        setTopSkuPage(1);
        setTopListingVisibleCount(TOP_ITEMS_BATCH_SIZE);
        setTopCompactSkuVisibleCount(TOP_ITEMS_BATCH_SIZE);
        setTopSkuVisibleCount(TOP_ITEMS_BATCH_SIZE);
    }, [selectedItem?.id]);

    useEffect(() => {
        setTopListingPage(1);
        setTopListingVisibleCount(TOP_ITEMS_BATCH_SIZE);
    }, [topListingSort.field, topListingSort.order]);

    useEffect(() => {
        setTopCompactSkuPage(1);
        setTopCompactSkuVisibleCount(TOP_ITEMS_BATCH_SIZE);
    }, [topCompactSkuSort.field, topCompactSkuSort.order]);

    useEffect(() => {
        setTopSkuPage(1);
        setTopSkuVisibleCount(TOP_ITEMS_BATCH_SIZE);
    }, [topSkuSort.field, topSkuSort.order]);

    useEffect(() => {
        if (selectedItem?.id) loadDistribution(selectedItem, distributionMode);
    }, [selectedItem?.id, distributionMode, loadDistribution]);

    useEffect(() => {
        if (selectedItem?.id) loadQuoteDistribution(selectedItem);
    }, [selectedItem?.id, loadQuoteDistribution]);

    useEffect(() => {
        if (!isShortDateRangeFilter || !selectedItem?.id || !analysisScopeKey) return;
        if (analysisDetailsLoadedKey !== analysisScopeKey) return;
        if (loadingDetails || loadingDistribution || loadingQuoteDistribution) return;
        if (autoCompareActivatedKey === analysisScopeKey) return;

        if (comparisonTrendMode === 'custom' && (!comparisonTrendRange?.[0] || !comparisonTrendRange?.[1])) {
            setComparisonTrendMode('30');
        }
        setShowComparisonTrend(true);
        setAutoCompareActivatedKey(analysisScopeKey);
    }, [
        analysisDetailsLoadedKey,
        analysisScopeKey,
        autoCompareActivatedKey,
        comparisonTrendMode,
        comparisonTrendRange,
        isShortDateRangeFilter,
        loadingDetails,
        loadingDistribution,
        loadingQuoteDistribution,
        selectedItem?.id
    ]);

    useEffect(() => {
        if (selectedItem?.id && showComparisonTrend) {
            loadComparisonTrend(selectedItem);
        }
    }, [selectedItem?.id, showComparisonTrend, comparisonTrendMode, comparisonTrendRange, loadComparisonTrend]);

    const objectSelectOptions = useMemo(() => objects.map(item => ({
        value: String(item.id),
        label: objectType === 'niche' ? (item.path || item.name || item.id) : (item.name || item.type || item.id)
    })), [objectType, objects]);
    const nicheSelectOptions = useMemo(() => filterObjects.niche.map(item => ({
        value: String(item.id),
        label: item.path || item.name || item.type || item.id
    })), [filterObjects.niche]);
    const quoteSelectOptions = useMemo(() => filterObjects.quote.map(item => ({
        value: String(item.id),
        label: item.name || item.type || item.id
    })), [filterObjects.quote]);
    const categorySelectOptions = useMemo(() => filterObjects.category.map(item => ({
        value: String(item.id),
        label: item.name || item.type || item.id
    })), [filterObjects.category]);
    const productLineSelectOptions = useMemo(() => filterObjects.productLine.map(item => ({
        value: String(item.id),
        label: item.name || item.id
    })), [filterObjects.productLine]);
    const currentObjectLabel = useMemo(() => (
        OBJECT_OPTIONS.find(item => item.value === objectType)?.label || 'Object'
    ), [objectType]);
    const analysisContext = useMemo(() => {
        const summarize = (ids, options) => {
            const labels = ids
                .map(id => options.find(option => String(option.value) === String(id))?.label)
                .filter(Boolean);
            if (labels.length === ids.length) return labels.join(', ');
            return `${ids.length} selected`;
        };
        const filters = [];
        if (nicheIds.length) filters.push({ key: 'niche', label: `Niche: ${summarize(nicheIds, nicheSelectOptions)}` });
        if (quoteIds.length) filters.push({ key: 'quote', label: `Quote: ${summarize(quoteIds, quoteSelectOptions)}` });
        if (categoryIds.length) filters.push({ key: 'category', label: `Category: ${summarize(categoryIds, categorySelectOptions)}` });
        if (productLineIds.length) filters.push({ key: 'productline', label: `Product Line: ${summarize(productLineIds, productLineSelectOptions)}` });

        const period = analysisRange.dateFrom && analysisRange.dateTo
            ? `${formatMetricDate(analysisRange.dateFrom)} - ${formatMetricDate(analysisRange.dateTo)}`
            : `${days} days`;
        return { filters, period };
    }, [
        analysisRange,
        categoryIds,
        categorySelectOptions,
        days,
        nicheIds,
        nicheSelectOptions,
        productLineIds,
        productLineSelectOptions,
        quoteIds,
        quoteSelectOptions
    ]);
    const comparisonPeriod = useMemo(() => {
        if (!analysisRange.dateFrom || !analysisRange.dateTo) {
            return {
                shortLabel: `prev ${days}d`,
                fullLabel: `Previous ${days} days`
            };
        }

        const currentStart = dayjs(analysisRange.dateFrom);
        const currentEnd = dayjs(analysisRange.dateTo);
        const periodDays = getInclusiveDays(currentStart, currentEnd);
        const previousEnd = currentStart.subtract(1, 'day');
        const previousStart = previousEnd.subtract(periodDays - 1, 'day');
        return {
            shortLabel: `prev ${periodDays}d`,
            fullLabel: `${formatMetricDate(previousStart.format('YYYY-MM-DD'))} - ${formatMetricDate(previousEnd.format('YYYY-MM-DD'))}`
        };
    }, [analysisRange.dateFrom, analysisRange.dateTo, days]);

    const resetOverviewTree = () => {
        setSelectedItem(null);
        setExpandedChildrenById({});
        setOverviewExpansionHistory([]);
        setExpandedTinyGroups({});
    };

    const handleExpandAllNichesChange = (event) => {
        setExpandAllNiches(event.target.checked);
        setSelectedItem(null);
        resetOverviewTree();
    };

    const toggleTinyGroup = useCallback((item) => {
        if (!item?.isTinyGroup) return;
        const itemId = String(item.id);
        setExpandedTinyGroups(current => ({
            ...current,
            [itemId]: !current[itemId]
        }));
    }, []);

    const backOverviewTree = useCallback(() => {
        const targetId = overviewExpansionHistory[overviewExpansionHistory.length - 1];
        if (!targetId) return;
        setExpandedChildrenById(childrenById => {
            const next = { ...childrenById };
            delete next[targetId];
            return next;
        });
        setOverviewExpansionHistory(current => current.slice(0, -1));
    }, [overviewExpansionHistory]);

    const renderOverviewChartToolbar = () => {
        if (objectType !== 'niche') return null;
        const disabled = expandAllNiches || !overviewExpansionHistory.length || loadingOverview;
        return (
            <div className="overview-chart-toolbar">
                <Checkbox
                    className="overview-expand-all-checkbox"
                    checked={expandAllNiches}
                    onChange={handleExpandAllNichesChange}
                >
                    Expand all
                </Checkbox>
                <AntTooltip title="Back one step">
                    <Button
                        size="small"
                        icon={<ArrowLeftOutlined />}
                        disabled={disabled}
                        onClick={backOverviewTree}
                    />
                </AntTooltip>
                <AntTooltip title="Reset to main niches">
                    <Button
                        size="small"
                        icon={<ReloadOutlined />}
                        disabled={disabled}
                        onClick={resetOverviewTree}
                    />
                </AntTooltip>
            </div>
        );
    };

    const renderBubbleChart = () => {
        const tooltip = hoveredBubble;
        const tooltipWidth = 260;
        const tooltipHeight = getOverviewSourcePath(tooltip).length ? 82 : 62;
        const tooltipX = tooltip
            ? Math.max(12, Math.min(1000 - tooltipWidth - 12, tooltip.x + tooltip.r + 14))
            : 0;
        const tooltipY = tooltip
            ? Math.max(12, Math.min(1000 - tooltipHeight - 12, tooltip.y - tooltipHeight / 2))
            : 0;
        const tooltipPath = tooltip ? formatOverviewPath(tooltip) : '';

        return (
            <div className="overview-bubble-square overview-bubble-square--modal" onMouseLeave={() => setHoveredBubble(null)}>
                <svg className="overview-bubble-svg" viewBox="0 0 1000 1000" role="img" aria-label={`${currentObjectLabel} bubble distribution`}>
                    {modalBubbleNodes.map((item, index) => {
                        const canExpand = objectType === 'niche' && item.id && !item.isOther && !item.isTinyGroup && item.hasChildren;
                        const canAnalyze = objectType !== 'niche' && item.id && !item.isOther && !item.isTinyGroup;
                        const isSelected = item.id && String(selectedItem?.id) === String(item.id);
                        const isExpanding = item.id && String(expandingOverviewId) === String(item.id);
                        const displaySourcePath = getOverviewSourcePath(item);
                        const showPath = displaySourcePath.length && item.r >= 92;
                        const showName = item.r >= 28;
                        const showShare = item.r >= 38;
                        const showSales = item.r >= 66;
                        const maxLabelLength = Math.max(4, Math.floor(item.r / 4.5));
                        const labelRows = [
                            showPath ? {
                                key: 'path',
                                className: 'overview-bubble-label overview-bubble-label-path',
                                text: truncateTreemapLabel(displaySourcePath.join(' / '), Math.max(6, maxLabelLength - 1)),
                                gap: 20
                            } : null,
                            showName ? {
                                key: 'name',
                                className: 'overview-bubble-label overview-bubble-label-name',
                                text: truncateTreemapLabel(getOverviewDisplayName(item), maxLabelLength),
                                gap: 25
                            } : null,
                            showShare ? {
                                key: 'share',
                                className: 'overview-bubble-label overview-bubble-label-share',
                                text: `${Number(item.percentage || 0).toFixed(1)}%`,
                                gap: 28
                            } : null,
                            showSales ? {
                                key: 'sales',
                                className: 'overview-bubble-label overview-bubble-label-sales',
                                text: `${Number(item.value || 0).toLocaleString()} sales`,
                                gap: 0
                            } : null
                        ].filter(Boolean);
                        const totalLabelHeight = labelRows.reduce((sum, row, rowIndex) => (
                            sum + (rowIndex < labelRows.length - 1 ? row.gap : 0)
                        ), 0);
                        const firstLabelY = item.y - totalLabelHeight / 2;

                        return (
	                            <g
	                                key={item.id || item.name || index}
	                                className={`overview-bubble-node ${canExpand || canAnalyze ? 'clickable' : ''} ${isSelected ? 'selected' : ''} ${isExpanding ? 'expanding' : ''}`}
	                                onClick={() => {
	                                    if (canExpand) {
	                                        setOverviewExpandRequest(item);
	                                        return;
	                                    }
	                                    if (canAnalyze) handleAnalyzeOverviewItem(item);
	                                }}
	                                onMouseEnter={() => setHoveredBubble(item)}
                                onMouseMove={() => setHoveredBubble(item)}
                            >
                                <circle
                                    cx={item.x}
                                    cy={item.y}
                                    r={item.r}
                                    fill={item.color || COLORS[index % COLORS.length]}
                                />
                                {labelRows.map((row, rowIndex) => {
                                    const rowOffset = labelRows
                                        .slice(0, rowIndex)
                                        .reduce((sum, previousRow) => sum + previousRow.gap, 0);
                                    return (
                                        <text
                                            key={row.key}
                                            x={item.x}
                                            y={firstLabelY + rowOffset}
                                            className={row.className}
                                        >
                                            {row.text}
                                        </text>
                                    );
                                })}
                            </g>
                        );
                    })}
                    {tooltip ? (
                        <g className="overview-bubble-tooltip">
                            <rect
                                x={tooltipX}
                                y={tooltipY}
                                width={tooltipWidth}
                                height={tooltipHeight}
                                rx={7}
                                ry={7}
                            />
                            <text x={tooltipX + 12} y={tooltipY + 22} className="overview-bubble-tooltip-title">
                            {truncateTreemapLabel(getOverviewDisplayName(tooltip), 28)}
                            </text>
                            {getOverviewSourcePath(tooltip).length ? (
                                <text x={tooltipX + 12} y={tooltipY + 42} className="overview-bubble-tooltip-path">
                                    {truncateTreemapLabel(tooltipPath, 35)}
                                </text>
                            ) : null}
                            <text x={tooltipX + 12} y={tooltipY + tooltipHeight - 16} className="overview-bubble-tooltip-value">
                                {`${Number(tooltip.value || 0).toLocaleString()} sales (${Number(tooltip.percentage || 0).toFixed(1)}%)`}
                            </text>
                        </g>
                    ) : null}
                </svg>
            </div>
        );
    };

    const handleOverviewItemClick = useCallback(async (item) => {
        if (!item || item.isOther || item.isTinyGroup) return;
        if (objectType !== 'niche') {
            handleAnalyzeOverviewItem(item);
            return;
        }
        if (!item.hasChildren) return;

        const itemId = String(item.id);
        if (expandedChildrenById[itemId]) return;
        if (expandingOverviewId) return;

        setLoadingOverview(true);
        setExpandingOverviewId(itemId);
        setOverviewComparisons({});
        setLoadingOverviewComparisons(false);
        setExpandedTinyGroups({});
        try {
            const sourcePath = [...(item.sourcePath || []), item.name || item.type || item.id];
            const { rows } = await fetchOverviewRows(item.id, sourcePath);
            setExpandedChildrenById(current => ({
                ...current,
                [itemId]: rows
            }));
            setOverviewExpansionHistory(current => current.includes(itemId) ? current : [...current, itemId]);
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to load child niches');
        } finally {
            setLoadingOverview(false);
            setExpandingOverviewId(null);
        }
    }, [expandedChildrenById, expandingOverviewId, fetchOverviewRows, handleAnalyzeOverviewItem, objectType]);

    useEffect(() => {
        if (!overviewExpandRequest) return;
        handleOverviewItemClick(overviewExpandRequest);
        setOverviewExpandRequest(null);
    }, [handleOverviewItemClick, overviewExpandRequest]);

    const handleObjectTypeChange = (value) => {
        setObjectType(value);
        setSelectedIds([]);
        setSelectedItem(null);
        resetOverviewTree();
        updateUrl({ objectType: value, selectedIds: [] });
    };

    const handleIdsChange = (value) => {
        setSelectedIds(value);
        setSelectedItem(null);
        resetOverviewTree();
        updateUrl({ selectedIds: value });
    };

    const handlePeriodRangeChange = (dates) => {
        if (!dates || dates.length !== 2 || !dates[0] || !dates[1]) {
            setDays(30);
            setDateFrom('');
            setDateTo('');
            setSelectedItem(null);
            resetOverviewTree();
            updateUrl({ days: 30, dateFrom: '', dateTo: '' });
            return;
        }

        const start = dates[0].startOf('day');
        const end = dates[1].startOf('day');
        const matchedRollingDays = ROLLING_PERIODS.find(periodDays => {
            const [rollingStart, rollingEnd] = getRollingRange(periodDays).map(value => value.startOf('day'));
            return start.isSame(rollingStart, 'day') && end.isSame(rollingEnd, 'day');
        });

        setSelectedItem(null);
        resetOverviewTree();

        if (matchedRollingDays) {
            setDays(matchedRollingDays);
            setDateFrom('');
            setDateTo('');
            updateUrl({ days: matchedRollingDays, dateFrom: '', dateTo: '' });
            return;
        }

        const nextDays = getInclusiveDays(start, end);
        const nextDateFrom = formatDateParam(start);
        const nextDateTo = formatDateParam(end);
        setDays(nextDays);
        setDateFrom(nextDateFrom);
        setDateTo(nextDateTo);
        updateUrl({ days: nextDays, dateFrom: nextDateFrom, dateTo: nextDateTo });
    };

    const handleNicheIdsChange = (value) => {
        setNicheIds(value);
        setSelectedItem(null);
        resetOverviewTree();
        updateUrl({ nicheIds: value });
    };

    const handleQuoteIdsChange = (value) => {
        setQuoteIds(value);
        setSelectedItem(null);
        resetOverviewTree();
        updateUrl({ quoteIds: value });
    };

    const handleCategoryIdsChange = (value) => {
        setCategoryIds(value);
        setSelectedItem(null);
        resetOverviewTree();
        updateUrl({ categoryIds: value });
    };

    const handleProductLineIdsChange = (value) => {
        setProductLineIds(value);
        setSelectedItem(null);
        resetOverviewTree();
        updateUrl({ productLineIds: value });
    };

    const buildChartData = (result = {}) => {
        const salesByDate = new Map((result.salesTrend || []).map(item => [item.date, item]));
        const rankByDate = new Map((result.rankTrend || []).map(item => [item.date, item]));
        const priceByDate = new Map((result.priceHistory || []).map(item => [item.date, item]));
        const dates = [...new Set([
            ...salesByDate.keys(),
            ...rankByDate.keys(),
            ...priceByDate.keys()
        ])].sort();

        return dates.map(dateValue => {
            const [, month, day] = String(dateValue).split('-').map(Number);
            const sales = salesByDate.get(dateValue);
            const rank = rankByDate.get(dateValue);
            const price = priceByDate.get(dateValue);
            return {
                date: month && day ? `${month}/${day}` : dateValue,
                fullDate: dateValue,
                sales: sales?.salesCount ?? 0,
                rank: rank?.mainRank ?? null,
                normalizedRank: rank?.normalizedRank ?? null,
                price: price?.price ?? null
            };
        });
    };

    const loadItemChart = async (scope, nextDays = chartDays) => {
        const params = new URLSearchParams({ level: scope.level, days: String(nextDays) });
        if (scope.sku) params.set('sku', scope.sku);
        if (scope.groupedSku) params.set('groupedSku', scope.groupedSku);
        const response = await api.get(`/inventory/listings/${encodeURIComponent(scope.parentSku)}/chart?${params.toString()}`);
        setChartModal(current => ({
            ...current,
            data: buildChartData(response.data || {})
        }));
    };

    const openItemTrend = async (record, metric) => {
        const sku = record?.sku;
        if (!sku) return;
        const scope = record._chartLevel === 'parent'
            ? { parentSku: sku, level: 'parent' }
            : record._chartLevel === 'grouped'
                ? { parentSku: record.parentSku, level: 'grouped', groupedSku: record.groupedSku || sku }
                : { parentSku: sku, level: 'leaf', sku };
        if (!scope.parentSku) return;
        try {
            setChartScope(scope);
            setChartDays(days);
            setChartVisibleMetrics(['sales', 'rank', 'price']);
            setChartModal({
                open: true,
                title: `Sales / Rank / Price - ${sku}`,
                data: []
            });
            await loadItemChart(scope, days);
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to load trend');
        }
    };

    const handleChartDaysChange = async (value) => {
        setChartDays(value);
        if (!chartScope) return;
        try {
            await loadItemChart(chartScope, value);
        } catch (error) {
            message.error(error.response?.data?.message || 'Failed to load trend');
        }
    };

    const openListingSearch = (sku) => {
        if (!sku) return;
        window.open(`/inventory/listings?searchText=${encodeURIComponent(sku)}`, '_blank', 'noopener,noreferrer');
    };

    const comparison = (value) => {
        if (value === null || value === undefined) {
            return <Text type="secondary">N/A</Text>;
        }
        const isPositive = value >= 0;
        return (
            <span className={`comparison-value ${isPositive ? 'comparison-positive' : 'comparison-negative'}`}>
                <span className="comparison-icon">{isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}</span>
                <span className="comparison-number">{Math.abs(value)}%</span>
            </span>
        );
    };
    const renderPeriodColumnTitle = () => (
        <AntTooltip title={`Compared with ${comparisonPeriod.fullLabel}`}>
            <span className="period-comparison-title">
                <span>vs Period</span>
                <span className="period-comparison-note">{comparisonPeriod.shortLabel}</span>
            </span>
        </AntTooltip>
    );

    const renderPeriodStatisticTitle = () => (
        <AntTooltip title={`Compared with ${comparisonPeriod.fullLabel}`}>
            <span className="period-stat-title">
                <span>vs Period</span>
                <span className="period-comparison-note">{comparisonPeriod.shortLabel}</span>
            </span>
        </AntTooltip>
    );

    const renderComparisonStatistic = (title, comparisonValue) => {
        if (comparisonValue?.value === null || comparisonValue?.value === undefined) {
            return <Statistic title={title} value="N/A" valueStyle={{ color: '#8c8c8c' }} />;
        }

        return (
            <Statistic
                title={title}
                value={Math.abs(comparisonValue.value)}
                suffix="%"
                prefix={comparisonValue.isPositive ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                valueStyle={{ color: comparisonValue.isPositive ? '#10b981' : '#ef4444' }}
            />
        );
    };

    const getControlledSortOrder = (sortState, field) => (
        sortState.field === field ? sortState.order : null
    );

    const normalizeTopItemSorter = (sorter) => {
        const activeSorter = Array.isArray(sorter) ? sorter.find(item => item?.order) : sorter;
        const field = activeSorter?.field || activeSorter?.columnKey;
        if (!activeSorter?.order || !TOP_ITEM_SORT_FIELDS.includes(field)) {
            return DEFAULT_TOP_ITEM_SORT;
        }
        return { field, order: activeSorter.order };
    };

    const handleTopListingTableChange = (pagination, filters, sorter) => {
        setTopListingSort(normalizeTopItemSorter(sorter));
    };

    const handleTopCompactSkuTableChange = (pagination, filters, sorter) => {
        setTopCompactSkuSort(normalizeTopItemSorter(sorter));
    };

    const handleTopSkuTableChange = (pagination, filters, sorter) => {
        setTopSkuSort(normalizeTopItemSorter(sorter));
    };

    const handleTopListingPageChange = (page) => {
        setTopListingPage(page);
        setTopListingVisibleCount(TOP_ITEMS_BATCH_SIZE);
    };

    const handleTopCompactSkuPageChange = (page) => {
        setTopCompactSkuPage(page);
        setTopCompactSkuVisibleCount(TOP_ITEMS_BATCH_SIZE);
    };

    const handleTopSkuPageChange = (page) => {
        setTopSkuPage(page);
        setTopSkuVisibleCount(TOP_ITEMS_BATCH_SIZE);
    };

    const handleTopListingLoadMore = () => {
        setTopListingVisibleCount(count => count + TOP_ITEMS_BATCH_SIZE);
    };

    const handleTopCompactSkuLoadMore = () => {
        setTopCompactSkuVisibleCount(count => count + TOP_ITEMS_BATCH_SIZE);
    };

    const handleTopSkuLoadMore = () => {
        setTopSkuVisibleCount(count => count + TOP_ITEMS_BATCH_SIZE);
    };

    const renderSellerCell = (_, record) => {
        const sellerText = record.sellerName || record.sellerCode || '-';
        return (
            <span title={sellerText} style={{ display: 'inline-block', maxWidth: 96, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {sellerText}
            </span>
        );
    };

    const buildProductColumns = (sortState) => [
        {
            title: 'SKU / ASIN',
            dataIndex: 'sku',
            key: 'sku',
            sorter: true,
            sortDirections: TOP_ITEM_SORT_DIRECTIONS,
            sortOrder: getControlledSortOrder(sortState, 'sku'),
            render: (sku, record) => (
                <div className="niche-sku-cell">
                    <Button
                        type="link"
                        className="niche-sku-link"
                        title={sku || '-'}
                        onClick={() => openListingSearch(sku)}
                    >
                        {sku || '-'}
                    </Button>
                    <Row className="niche-sku-detail-row" style={{ flexFlow: 'nowrap' }} align="middle" gutter={8}>
                        <Col flex="none">
                            <ProductImagesGallery
                                skuItem={{ ...record, images: normalizeProductImages(record) }}
                                maxVisibleImages={1}
                                showRemainingCount={false}
                                imageSize={42}
                            />
                        </Col>
                        <Col flex="auto" className="niche-sku-text">
                            <div className="niche-sku-asin">{record.asin || '-'}</div>
                            {record.title ? (
                                <button
                                    type="button"
                                    className="niche-sku-title niche-sku-title-link"
                                    onClick={() => openListingSearch(sku)}
                                >
                                    {record.title}
                                </button>
                            ) : (
                                <div className="niche-sku-title">-</div>
                            )}
                        </Col>
                    </Row>
                </div>
            )
        },
        {
            title: 'Seller',
            dataIndex: 'sellerName',
            key: 'sellerName',
            width: 112,
            align: 'center',
            render: renderSellerCell
        },
        {
            title: 'Sales',
            dataIndex: 'currentSales',
            key: 'currentSales',
            width: 90,
            align: 'center',
            sorter: true,
            sortDirections: TOP_ITEM_SORT_DIRECTIONS,
            sortOrder: getControlledSortOrder(sortState, 'currentSales'),
            render: (value, record) => (
                <Button type="link" onClick={() => openItemTrend(record, 'sales')}>{value || 0}</Button>
            )
        },
        {
            title: renderPeriodColumnTitle,
            dataIndex: 'monthComparison',
            key: 'monthComparison',
            width: 100,
            align: 'center',
            sorter: true,
            sortDirections: TOP_ITEM_SORT_DIRECTIONS,
            sortOrder: getControlledSortOrder(sortState, 'monthComparison'),
            render: comparison
        },
        {
            title: 'vs Year',
            dataIndex: 'yearComparison',
            key: 'yearComparison',
            width: 100,
            align: 'center',
            sorter: true,
            sortDirections: TOP_ITEM_SORT_DIRECTIONS,
            sortOrder: getControlledSortOrder(sortState, 'yearComparison'),
            render: comparison
        },
        {
            title: 'Rank',
            dataIndex: 'rank',
            key: 'rank',
            width: 120,
            align: 'center',
            sorter: true,
            sortDirections: TOP_ITEM_SORT_DIRECTIONS,
            sortOrder: getControlledSortOrder(sortState, 'rank'),
            render: (value, record) => (
                <Button
                    type="link"
                    onClick={() => openItemTrend(record, 'rank')}
                    style={{ height: 'auto', padding: 0, lineHeight: 1.3 }}
                >
                    <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <span style={{ color: '#262626', fontWeight: 600 }}>
                            {value ? `#${Number(value).toLocaleString()}` : '-'}
                        </span>
                        {record.normalizedRank ? (
                            <span style={{ color: '#1677ff', fontSize: 12 }}>
                                <SwapOutlined style={{ marginRight: 4 }} />
                                #{Number(record.normalizedRank).toLocaleString()}
                            </span>
                        ) : null}
                    </span>
                </Button>
            )
        }
    ];

    const quoteColumns = [
        { title: 'Quote', dataIndex: 'tag', key: 'tag' },
        { title: 'Sales', dataIndex: 'totalSales', width: 100, align: 'center' }
    ];

    const renderTopItemsTable = ({ title, rows, visibleRows, currentPage, visibleCount, onPageChange, onLoadMore, sortState, onTableChange }) => {
        const hasPagination = rows.length > TOP_ITEMS_BATCH_SIZE;
        const displayStart = rows.length ? ((currentPage - 1) * TOP_ITEMS_BATCH_SIZE) + 1 : 0;
        const displayEnd = Math.min(displayStart + Math.max(visibleRows.length - 1, 0), rows.length);
        const canLoadMore = hasPagination && displayStart + visibleCount - 1 < rows.length;

        return (
            <Card
                size="small"
                title={title}
                className="top-items-card"
                extra={<Text type="secondary">{displayStart}-{displayEnd}/{rows.length}</Text>}
            >
                <Table
                    className="niche-table top-items-table"
                    size="small"
                    columns={buildProductColumns(sortState)}
                    dataSource={visibleRows}
                    pagination={false}
                    scroll={{ x: 620 }}
                    showSorterTooltip={false}
                    onChange={onTableChange}
                    rowKey={(record) => `${record._chartLevel}-${record.id || record.parentSku || ''}-${record.sku || record.asin || record.groupedSku || ''}`}
                />
                {hasPagination ? (
                    <div className="top-table-pagination">
                        <Pagination
                            size="small"
                            current={currentPage}
                            pageSize={TOP_ITEMS_BATCH_SIZE}
                            total={rows.length}
                            showSizeChanger={false}
                            showLessItems
                            onChange={onPageChange}
                        />
                        <Button
                            size="small"
                            disabled={!canLoadMore}
                            onClick={onLoadMore}
                        >
                            Load more
                        </Button>
                    </div>
                ) : null}
            </Card>
        );
    };

    const renderSalesTrendChart = (data, height = 260) => (
        <ResponsiveContainer width="100%" height={height}>
            <LineChart data={data} margin={{ top: 20, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    tickFormatter={(date) => {
                        const [, month, day] = String(date).split('-').map(Number);
                        return month && day ? `${month}/${day}` : date;
                    }}
                />
                <YAxis />
                <RechartsTooltip />
                <Line type="monotone" dataKey="salesCount" stroke="#1677ff" strokeWidth={2} dot={{ r: 2 }}>
                    <LabelList
                        dataKey="peakLabel"
                        position="top"
                        offset={8}
                        fill="#1677ff"
                        fontSize={11}
                        fontWeight={600}
                    />
                </Line>
            </LineChart>
        </ResponsiveContainer>
    );

    const renderComparisonTrendCard = () => (
        <Card
            size="small"
            className="trend-card comparison-trend-card"
            title="Comparison Sales Trend"
            extra={(
                <div className="comparison-trend-controls">
                    <Segmented
                        size="small"
                        value={comparisonTrendMode}
                        onChange={setComparisonTrendMode}
                        options={[
                            { label: '7D', value: '7' },
                            { label: '30D', value: '30' },
                            { label: '90D', value: '90' },
                            { label: 'Custom', value: 'custom' }
                        ]}
                    />
                    {comparisonTrendMode === 'custom' ? (
                        <RangePicker
                            size="small"
                            value={comparisonTrendRange}
                            onChange={setComparisonTrendRange}
                            allowClear={false}
                        />
                    ) : null}
                </div>
            )}
        >
            <Spin spinning={loadingComparisonTrend}>
                {comparisonTrendChartData.length ? (
                    renderSalesTrendChart(comparisonTrendChartData, 240)
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Select a comparison range" />
                )}
            </Spin>
        </Card>
    );

    const renderDistributionShare = (section, showModeSwitch = false) => {
        const rows = (section.rows || [])
            .filter(row => Number(row.value) > 0)
            .sort((a, b) => Number(b.value || 0) - Number(a.value || 0));
        const total = rows.reduce((sum, row) => sum + Number(row.value || 0), 0);
        const visibleRows = rows.slice(0, 8).map((row, index) => ({
            ...row,
            color: row.color || COLORS[index % COLORS.length],
            share: total ? (Number(row.value || 0) / total) * 100 : 0
        }));
        const otherTotal = rows.slice(8).reduce((sum, row) => sum + Number(row.value || 0), 0);
        const displayRows = otherTotal > 0
            ? [
                ...visibleRows,
                {
                    id: `${section.objectType}-other`,
                    name: 'Other',
                    value: otherTotal,
                    color: '#94a3b8',
                    share: (otherTotal / total) * 100
                }
            ]
            : visibleRows;

        return (
            <div className="distribution-share-section" key={section.objectType}>
                <div className="distribution-share-header">
                    <span className="distribution-share-title">
                        <Text strong>{showModeSwitch ? 'By' : formatDistributionTitle(section.objectType)}</Text>
                        {showModeSwitch ? (
                            <Segmented
                                size="small"
                                value={distributionMode}
                                options={DISTRIBUTION_OPTIONS}
                                onChange={value => setDistributionMode(value)}
                            />
                        ) : null}
                    </span>
                    <span className="distribution-share-header-actions">
                        <Text type="secondary">{total.toLocaleString()} sales</Text>
                    </span>
                </div>
                {displayRows.length ? (
                    <>
                        <div className="distribution-stacked-bar" aria-label={`Distribution by ${section.objectType}`}>
                            {displayRows.map(row => (
                                <AntTooltip
                                    key={row.id || row.name}
                                    title={`${row.name}: ${Number(row.value || 0).toLocaleString()} sales (${row.share.toFixed(1)}%)`}
                                >
                                    <div
                                        className="distribution-stacked-segment"
                                        style={{
                                            width: `${Math.max(row.share, 1.5)}%`,
                                            backgroundColor: row.color
                                        }}
                                    />
                                </AntTooltip>
                            ))}
                        </div>
                        <div className="distribution-share-list">
                            {displayRows.map(row => (
                                <div className="distribution-share-row" key={row.id || row.name}>
                                    <span className="distribution-share-label">
                                        <span className="distribution-share-dot" style={{ backgroundColor: row.color }} />
                                        <span className="distribution-share-name">{row.name}</span>
                                    </span>
                                    <span className="distribution-share-value">
                                        {row.share.toFixed(1)}% <Text type="secondary">({Number(row.value || 0).toLocaleString()})</Text>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
            </div>
        );
    };

    const renderDistributions = () => (
        <Card
            size="small"
            title="Distribution share"
            style={{ marginBottom: 12 }}
        >
            <div className="distribution-share-grid">
                <Spin spinning={loadingDistribution}>
                    {renderDistributionShare(
                        distributions.find(section => section.objectType === distributionMode) || { objectType: distributionMode, rows: [] },
                        true
                    )}
                </Spin>
                <Spin spinning={loadingQuoteDistribution}>
                    {quoteDistribution ? (
                        renderDistributionShare(quoteDistribution)
                    ) : (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                    )}
                </Spin>
            </div>
        </Card>
    );

    return (
        <div className="niche-analysis">
            <Card size="small" className="analysis-filter-card">
                <div className="analysis-filter-primary">
                    <div className="analysis-filter-field analysis-filter-type">
                        <Text className="analysis-filter-label">Analysis type</Text>
                        <Select value={objectType} options={OBJECT_OPTIONS} style={{ width: '100%' }} onChange={handleObjectTypeChange} />
                    </div>
                    <div className="analysis-filter-field analysis-filter-objects">
                        <Text className="analysis-filter-label">Objects</Text>
                        <Select
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            placeholder="All objects"
                            value={selectedIds}
                            options={objectSelectOptions}
                            onChange={handleIdsChange}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className="analysis-filter-field analysis-filter-period">
                        <Text className="analysis-filter-label">Date range</Text>
                        <RangePicker
                            allowClear
                            value={periodRangeValue}
                            presets={periodPresets}
                            format="YYYY-MM-DD"
                            onChange={handlePeriodRangeChange}
                            style={{ width: '100%' }}
                        />
                    </div>
                </div>

                <div className="analysis-filter-separator" />
                <Text strong className="analysis-filter-section-title">SKU filters</Text>
                <div className="analysis-filter-cohort">
                    <div className="analysis-filter-field">
                        <Text className="analysis-filter-label">Niche</Text>
                        <Select
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            placeholder="All niches"
                            value={nicheIds}
                            options={nicheSelectOptions}
                            loading={Boolean(filterObjectsLoading.niche)}
                            onOpenChange={(open) => {
                                if (open) fetchObjectOptions('niche').catch(() => message.error('Failed to load niches'));
                            }}
                            onChange={handleNicheIdsChange}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className="analysis-filter-field">
                        <Text className="analysis-filter-label">Quote</Text>
                        <Select
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            placeholder="All quotes"
                            value={quoteIds}
                            options={quoteSelectOptions}
                            loading={Boolean(filterObjectsLoading.quote)}
                            onOpenChange={(open) => {
                                if (open) fetchObjectOptions('quote').catch(() => message.error('Failed to load quotes'));
                            }}
                            onChange={handleQuoteIdsChange}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className="analysis-filter-field">
                        <Text className="analysis-filter-label">Category</Text>
                        <Select
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            placeholder="All categories"
                            value={categoryIds}
                            options={categorySelectOptions}
                            loading={Boolean(filterObjectsLoading.category)}
                            onOpenChange={(open) => {
                                if (open) fetchObjectOptions('category').catch(() => message.error('Failed to load categories'));
                            }}
                            onChange={handleCategoryIdsChange}
                            style={{ width: '100%' }}
                        />
                    </div>
                    <div className="analysis-filter-field">
                        <Text className="analysis-filter-label">Product Line</Text>
                        <Select
                            mode="multiple"
                            allowClear
                            maxTagCount="responsive"
                            placeholder="All product lines"
                            value={productLineIds}
                            options={productLineSelectOptions}
                            loading={Boolean(filterObjectsLoading.productLine)}
                            onOpenChange={(open) => {
                                if (open) fetchObjectOptions('productLine').catch(() => message.error('Failed to load product lines'));
                            }}
                            onChange={handleProductLineIdsChange}
                            style={{ width: '100%' }}
                        />
                    </div>
                </div>
            </Card>

            <div className="analysis-grid">
                <div className="chart-panel">
                    <div className="distribution-panel-header">
                        <Title level={4} style={{ margin: 0 }}>Distribution</Title>
                        {chartOverviewRows.length ? (
                            <Button size="small" icon={<EyeOutlined />} onClick={() => setOverviewDetailOpen(true)}>
                                View detail
                            </Button>
                        ) : null}
                    </div>
                    {displayOverview.length ? (
                        <div className={`overview-distribution-body ${loadingOverview ? 'loading' : ''}`}>
                            <div className="overview-compact-chart">
                                {renderOverviewChartToolbar()}
                                <div className="overview-treemap-square overview-treemap-square--compact">
                                    <ResponsiveContainer width="100%" height="100%">
                                        <Treemap
                                            data={modalTreemapRows}
                                            dataKey="value"
                                            nameKey="name"
                                            aspectRatio={1}
                                            stroke="#ffffff"
                                            content={renderTreemapContent}
                                            isAnimationActive={false}
                                        >
                                            <RechartsTooltip formatter={formatTreemapTooltip} />
                                        </Treemap>
                                    </ResponsiveContainer>
                                </div>
	                            </div>
		                            <Card
		                                size="small"
		                                className={`distribution-total-card split-analysis-card ${isOverviewTotalItem(selectedItem) ? 'selected' : ''}`}
	                                >
		                                <div className="split-analysis-row split-analysis-row--total split-analysis-row--single">
		                                    <button
		                                        type="button"
		                                        className="distribution-total-info split-analysis-main"
		                                        aria-label="Analyze total sales"
		                                        onClick={handleAnalyzeOverviewTotal}
		                                    >
		                                        <div className="distribution-total-main">
		                                            <span className="distribution-total-kicker">Overall scope</span>
		                                            <Text strong className="distribution-total-name">Total Sales</Text>
		                                            <AntTooltip title={`Compared with previous ${comparisonPeriodDays} days`}>
		                                                <span
		                                                    className={`niche-period-comparison distribution-total-comparison ${
		                                                        loadingOverviewComparisons
		                                                            ? 'loading'
		                                                            : Number(distributionTotalComparison.periodComparison || 0) > 0
		                                                                ? 'positive'
		                                                                : Number(distributionTotalComparison.periodComparison || 0) < 0
		                                                                    ? 'negative'
		                                                                    : 'neutral'
		                                                    }`}
		                                                >
		                                                    <span className="niche-period-label">{comparisonPeriodLabel}</span>
		                                                    {loadingOverviewComparisons ? (
		                                                        <Spin size="small" />
		                                                    ) : (
		                                                        <strong>{formatPeriodComparisonValue(distributionTotalComparison.periodComparison)}</strong>
		                                                    )}
		                                                </span>
		                                            </AntTooltip>
		                                        </div>
		                                        <div className="distribution-total-stats">
		                                            <span className="distribution-total-value">{distributionTotalSales.toLocaleString()}</span>
		                                            <span className="distribution-total-unit">sales</span>
		                                        </div>
		                                    </button>
		                                </div>
		                            </Card>
		                            <div className="niche-cards-grid">
		                                {cardOverviewRows.map((item, index) => (
		                                    <Card
		                                        key={`${item._tinyGroupParentId || 'row'}-${item.id}`}
		                                        size="small"
		                                        className={`niche-card split-analysis-card ${item.isOther ? 'summary' : ''} ${item.isTinyGroup ? 'tiny-group' : ''} ${item._isTinyGroupChild ? 'tiny-group-child' : ''} ${String(selectedItem?.id) === String(item.id) ? 'selected' : ''} ${String(expandingOverviewId) === String(item.id) ? 'expanding' : ''}`}
	                                    >
	                                        <div className="niche-indicator" style={{ backgroundColor: item.color || COLORS[index % COLORS.length] }} />
	                                        <div className="split-analysis-row">
				                                    <button
				                                        type="button"
				                                        className="niche-info split-analysis-main"
				                                        aria-label={`Analyze ${getOverviewDisplayName(item) || 'segment'}`}
				                                        disabled={item.isOther || item.isTinyGroup}
				                                        onClick={() => handleAnalyzeOverviewItem(item)}
				                                    >
			                                        <div className="niche-name-row">
				                                            {renderOverviewPathTags(item)}
			                                            <Text strong className="niche-name">{getOverviewDisplayName(item)}</Text>
			                                            <AntTooltip title={`Compared with previous ${comparisonPeriodDays} days`}>
		                                                <span
		                                                    className={`niche-period-comparison ${
		                                                        loadingOverviewComparisons
		                                                            ? 'loading'
		                                                            : Number(overviewComparisons[String(item.id)]?.periodComparison || 0) > 0
		                                                                ? 'positive'
		                                                                : Number(overviewComparisons[String(item.id)]?.periodComparison || 0) < 0
		                                                                    ? 'negative'
		                                                                    : 'neutral'
		                                                    }`}
		                                                >
		                                                    <span className="niche-period-label">{comparisonPeriodLabel}</span>
		                                                    {loadingOverviewComparisons ? (
		                                                        <Spin size="small" />
		                                                    ) : (
		                                                        <strong>{formatPeriodComparisonValue(overviewComparisons[String(item.id)]?.periodComparison)}</strong>
		                                                    )}
		                                                </span>
		                                            </AntTooltip>
		                                        </div>
			                                        <div className="niche-stats">
			                                            <span className="niche-percentage">{Number(item.percentage || 0).toFixed(1)}%</span>
			                                            <span className="niche-value">{Number(item.value || 0).toLocaleString()} sales</span>
			                                        </div>
		                                    </button>
                                                {item.isTinyGroup ? (
                                                    <AntTooltip title={expandedTinyGroups[String(item.id)] ? 'Hide grouped niches' : 'View grouped niches'}>
                                                        <button
                                                            type="button"
                                                            className="side-action-button child-niche-action"
                                                            aria-label={`${expandedTinyGroups[String(item.id)] ? 'Hide' : 'View'} niches grouped under ${getOverviewDisplayName(item) || 'small segments'}`}
                                                            onClick={() => toggleTinyGroup(item)}
                                                        >
                                                            <ApartmentOutlined className="side-action-icon" />
                                                        </button>
                                                    </AntTooltip>
                                                ) : !item.isOther && objectType === 'niche' && item.hasChildren ? (
                                                    <AntTooltip title="View child niches">
                                                        <button
                                                            type="button"
                                                            className="side-action-button child-niche-action"
                                                            aria-label={`View child niches for ${getOverviewDisplayName(item) || 'segment'}`}
                                                            onClick={() => handleOverviewItemClick(item)}
                                                        >
                                                            <ApartmentOutlined className="side-action-icon" />
                                                        </button>
                                                    </AntTooltip>
                                                ) : (
                                                    <span className="side-action-placeholder" aria-label="No child niches">-</span>
                                                )}
	                                        </div>
	                                    </Card>
	                                ))}
                            </div>
                            {loadingOverview ? (
                                <div className="overview-loading-overlay">
                                    <Spin size="small" />
                                    <span>Loading child niches</span>
                                </div>
                            ) : null}
                        </div>
                    ) : loadingOverview ? (
                        <Spin style={{ display: 'block', padding: 80 }} />
                    ) : <Empty description="No analytics data" />}
                </div>

	                <div className="details-panel">
	                    <div className="analysis-detail-heading">
	                        <Title level={4}>
	                            {selectedItem
	                                ? isOverviewTotalItem(selectedItem)
	                                    ? 'Overall Analysis: Total Sales'
	                                    : `${OBJECT_OPTIONS.find(item => item.value === objectType)?.label}: ${selectedItem.name}`
	                                : 'Analysis'}
	                        </Title>
                        {selectedItem ? (
                            <div className="analysis-context-tags">
                                <Tag color="blue">Period: {analysisContext.period}</Tag>
                                {analysisContext.filters.length ? (
                                    analysisContext.filters.map(filter => (
                                        <Tag key={filter.key}>{filter.label}</Tag>
                                    ))
                                ) : (
                                    <Tag>Scope: All eligible SKU</Tag>
                                )}
                            </div>
                        ) : null}
                    </div>
                    {selectedItem ? (
                        <Spin spinning={loadingDetails}>
                            <div className="summary-trend-row">
                                <div className="summary-metrics-panel">
                                    <div className="metrics-column">
                                        <Card size="small" className="metric-card"><Statistic title="Total Sales" value={stats?.totalSales || 0} /></Card>
                                        <Card size="small" className="metric-card">{renderComparisonStatistic(renderPeriodStatisticTitle(), stats?.monthComparison)}</Card>
                                        <Card size="small" className="metric-card">{renderComparisonStatistic('vs Last Year', stats?.yearComparison)}</Card>
                                    </div>
                                </div>
                                <div className="summary-trend-panel">
                                    <div className="trend-stack">
                                        {showComparisonTrend ? renderComparisonTrendCard() : null}
                                        <Card
                                            size="small"
                                            title={`${days}-Day Sales Trend`}
                                            className="trend-card"
                                            extra={(
                                                <Button
                                                    size="small"
                                                    icon={<SwapOutlined />}
                                                    type={showComparisonTrend ? 'primary' : 'default'}
                                                    onClick={() => setShowComparisonTrend(value => !value)}
                                                >
                                                    Compare
                                                </Button>
                                            )}
                                        >
                                            {renderSalesTrendChart(salesTrendChartData)}
                                        </Card>
                                    </div>
                                </div>
                            </div>

                            {renderDistributions()}

                            <Row gutter={[12, 12]}>
                                <Col xs={24} xl={12}>
                                    {renderTopItemsTable({
                                        title: 'Top Listings',
                                        rows: topListingRows,
                                        visibleRows: visibleTopListingRows,
                                        currentPage: topListingPage,
                                        visibleCount: topListingVisibleCount,
                                        onPageChange: handleTopListingPageChange,
                                        onLoadMore: handleTopListingLoadMore,
                                        sortState: topListingSort,
                                        onTableChange: handleTopListingTableChange
                                    })}
                                </Col>
                                <Col xs={24} xl={12}>
                                    {renderTopItemsTable({
                                        title: 'Top SKU Con Tinh gọn',
                                        rows: topCompactSkuRows,
                                        visibleRows: visibleTopCompactSkuRows,
                                        currentPage: topCompactSkuPage,
                                        visibleCount: topCompactSkuVisibleCount,
                                        onPageChange: handleTopCompactSkuPageChange,
                                        onLoadMore: handleTopCompactSkuLoadMore,
                                        sortState: topCompactSkuSort,
                                        onTableChange: handleTopCompactSkuTableChange
                                    })}
                                </Col>
                                <Col xs={24} xl={12}>
                                    {renderTopItemsTable({
                                        title: 'Top SKU',
                                        rows: topSkuRows,
                                        visibleRows: visibleTopSkuRows,
                                        currentPage: topSkuPage,
                                        visibleCount: topSkuVisibleCount,
                                        onPageChange: handleTopSkuPageChange,
                                        onLoadMore: handleTopSkuLoadMore,
                                        sortState: topSkuSort,
                                        onTableChange: handleTopSkuTableChange
                                    })}
                                </Col>
                                <Col xs={24} xl={12}>
                                    <Card size="small" title="Top Quotes">
                                        <Table size="small" columns={quoteColumns} dataSource={topQuotes} pagination={{ pageSize: 8, size: 'small' }} />
                                    </Card>
                                </Col>
                            </Row>
                        </Spin>
                    ) : (
                        <Empty description="Select one item to view details" />
                    )}
                </div>
            </div>

            <Modal
                title={`Full ${currentObjectLabel.toLowerCase()} distribution`}
                open={overviewDetailOpen}
                onCancel={() => setOverviewDetailOpen(false)}
                footer={null}
                width="72vw"
                destroyOnClose
            >
                {chartOverviewRows.length ? (
                    <div className={`overview-distribution-body overview-distribution-body--modal ${loadingOverview ? 'loading' : ''}`}>
                        <Row gutter={[18, 18]} className="overview-detail-modal">
                            <Col xs={24} lg={15}>
                                <div className="overview-distribution-column">
                                    <div className="overview-distribution-title-row">
                                        <Text strong className="overview-distribution-title">Main distribution</Text>
                                        <Segmented
                                            size="small"
                                            value={overviewDetailView}
                                            onChange={setOverviewDetailView}
                                            options={[
                                                { label: 'Treemap', value: 'treemap' },
                                                { label: 'Bubble', value: 'bubble' }
                                            ]}
                                        />
                                    </div>
                                    {renderOverviewChartToolbar()}
                                    {overviewDetailView === 'bubble' ? (
                                        renderBubbleChart()
                                    ) : (
                                        <div className="overview-treemap-square overview-treemap-square--modal">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <Treemap
                                                    data={modalTreemapRows}
                                                    dataKey="value"
                                                    nameKey="name"
                                                    aspectRatio={1}
                                                    stroke="#ffffff"
                                                    content={renderTreemapContent}
                                                    isAnimationActive={false}
                                                >
                                                    <RechartsTooltip formatter={formatTreemapTooltip} />
                                                </Treemap>
                                            </ResponsiveContainer>
                                        </div>
                                    )}
                                </div>
                            </Col>
                            <Col xs={24} lg={9}>
                                <div className="overview-modal-legend">
                                    <div className="overview-modal-legend-head">
                                        <Text strong className="overview-distribution-title">Legend</Text>
                                        <Text type="secondary">{chartOverviewRows.length} segments</Text>
                                    </div>
                                    <div className="overview-modal-legend-columns">
                                        <span>{currentObjectLabel}</span>
                                        <span>Sales</span>
                                        <span>Share</span>
                                    </div>
                                    <div className="overview-modal-legend-list">
                                        {chartOverviewRows.map((item, index) => (
                                            <div key={item.id || item.name || index} className="overview-modal-legend-row">
                                                <span className="overview-modal-legend-dot" style={{ backgroundColor: item.color || COLORS[index % COLORS.length] }} />
                                                <div className="overview-modal-legend-main">
                                                    <span
                                                        className={`overview-modal-legend-name ${item.isOther || item.isTinyGroup ? 'muted' : ''}`}
                                                        title={getOverviewDisplayName(item)}
                                                    >
                                                        {getOverviewDisplayName(item)}
                                                    </span>
                                                    {renderOverviewPathTags(item, 'overview-modal-legend-path')}
                                                </div>
                                                <span className="overview-modal-legend-sales">{Number(item.value || 0).toLocaleString()}</span>
                                                <span className="overview-modal-legend-share">{Number(item.percentage || 0).toFixed(1)}%</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </Col>
                        </Row>
                        {loadingOverview ? (
                            <div className="overview-loading-overlay">
                                <Spin size="small" />
                                <span>Loading child niches</span>
                            </div>
                        ) : null}
                    </div>
                ) : (
                    <Empty description="No niche distribution data" />
                )}
            </Modal>

            <Modal
                title={chartModal.title}
                open={chartModal.open}
                onCancel={() => {
                    setChartModal({ open: false, title: '', data: [] });
                    setChartScope(null);
                }}
                footer={null}
                width={800}
                destroyOnClose
            >
                <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                    <Row align="middle" gutter={8}>
                        <Col><Text type="secondary">Period</Text></Col>
                        <Col>
                            <Select
                                size="small"
                                value={chartDays}
                                style={{ width: 110 }}
                                onChange={handleChartDaysChange}
                                options={DAY_OPTIONS}
                            />
                        </Col>
                    </Row>
                    <Checkbox.Group
                        value={chartVisibleMetrics}
                        onChange={setChartVisibleMetrics}
                        options={[
                            { label: 'Sales', value: 'sales' },
                            { label: 'Rank', value: 'rank' },
                            { label: 'Price', value: 'price' }
                        ]}
                    />
                </Row>
                {chartModal.data.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={chartModal.data}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                            <YAxis
                                yAxisId="salesAxis"
                                width={68}
                                tick={{ fontSize: 12 }}
                                tickFormatter={(value) => Number(value).toLocaleString()}
                                hide={!chartVisibleMetrics.includes('sales')}
                                label={{
                                    value: 'Sales (units)',
                                    angle: -90,
                                    position: 'insideLeft',
                                    offset: 8,
                                    style: { textAnchor: 'middle', fill: '#18a058', fontSize: 12, fontWeight: 600 }
                                }}
                            />
                            <YAxis
                                yAxisId="rankAxis"
                                orientation="right"
                                width={72}
                                tick={{ fontSize: 12 }}
                                tickFormatter={(value) => `#${Number(value).toLocaleString()}`}
                                hide={!chartVisibleMetrics.includes('rank')}
                                reversed
                            />
                            <YAxis
                                yAxisId="priceAxis"
                                orientation="right"
                                width={72}
                                tick={{ fontSize: 12 }}
                                tickFormatter={(value) => `$${value}`}
                                hide={!chartVisibleMetrics.includes('price')}
                            />
                            <RechartsTooltip
                                formatter={(value, name) => {
                                    if (value === null || value === undefined) return ['-', name];
                                    if (name === 'rank') return [`#${Number(value).toLocaleString()}`, 'Rank'];
                                    if (name === 'price') return [`$${Number(value).toFixed(2)}`, 'Price'];
                                    return [`${value} units`, 'Sales'];
                                }}
                                labelFormatter={(label, payload) => payload?.[0]?.payload?.fullDate || label}
                            />
                            <Legend />
                            {chartVisibleMetrics.includes('sales') && (
                                <Line yAxisId="salesAxis" type="monotone" dataKey="sales" stroke="#18a058" strokeWidth={2} dot={{ r: 2 }} />
                            )}
                            {chartVisibleMetrics.includes('rank') && (
                                <Line yAxisId="rankAxis" type="monotone" dataKey="rank" stroke="#1890ff" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                            )}
                            {chartVisibleMetrics.includes('price') && (
                                <Line yAxisId="priceAxis" type="monotone" dataKey="price" stroke="#f59e0b" strokeWidth={2} dot={{ r: 2 }} connectNulls />
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <Empty description="No chart data" />
                )}
            </Modal>
        </div>
    );
};

export default NicheAnalysis;
